import { Image } from '@graphcommerce/image'
import {
  WaitForCustomer,
  useCustomerQuery,
  useOrderCardItemImages,
} from '@graphcommerce/magento-customer'
import { Money } from '@graphcommerce/magento-store'
import { useDateTimeFormat } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Box, SxProps, Theme, Tooltip } from '@mui/material'
import PageLink from 'next/link'
import React from 'react'
import { ItemArtworkManager } from '../../Artwork/ItemArtworkManager'
import { StandardButton } from '../../Button/ButtonStyles'
import { CartAddressMultiLine } from '../../Cart/CartAddressMultiLine/CartAddressMultiLine'
import { GetOrderSummaryDocument } from './GetOrderSummary.gql'

export type OrderSummaryProps = {
  orderNumbers: string[]
  children?: React.ReactNode
  sx?: SxProps<Theme>
} & OwnerState

type OwnerState = { editable?: boolean }

export const getEstShipDate = (order: { estimated_ship_date?: string }) => {
  if (!order || !order.estimated_ship_date) {
    return null
  }

  const orderShipDate = new Date(order.estimated_ship_date)
  const computedDateArry = orderShipDate.toDateString().split(' ')
  const dayNum =
    computedDateArry[2].indexOf('0') === 0 ? computedDateArry[2].substring(1) : computedDateArry[2]

  return `${computedDateArry[1]} ${dayNum}`
}

export function OrderSummary(props: OrderSummaryProps) {
  const { orderNumbers = [], children, editable } = props

  const ordersQuery = useCustomerQuery(GetOrderSummaryDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      pageSize: 5,
      currentPage: Number(1),
      numbers: orderNumbers,
    },
  })
  const orders = ordersQuery.data?.customer?.orders

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const images = useOrderCardItemImages(orders)
  const dateFormatter = useDateTimeFormat({ dateStyle: 'long' })
  if (!orders) return null

  const firstOrder = orders.items?.at(0)
  const shippingMethodDisp = firstOrder?.shipping_method
    ?.substring(
      // eslint-disable-next-line no-unsafe-optional-chaining
      firstOrder?.shipping_method?.indexOf('-') + 1,
      firstOrder?.shipping_method?.length,
    )
    ?.trim()

  // const { email, shipping_addresses, billing_address, is_virtual } = data.cart

  return (
    <div className='mt-8'>
      <WaitForCustomer waitFor={ordersQuery}>
        {orders.items.map((order, i) => (
          <div
            className='Type-Large-Regular mb-5 flex flex-col items-center justify-center gap-10 md:gap-5'
            key={order?.number}
          >
            <div className='block w-full md:hidden'>
              <div className='Type-Large-Regular flex w-full flex-col items-start gap-[4px] md:grid md:grid-cols-5 md:gap-[55px]'>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className='text-tight-black'>
                    <Trans id='Order Number' />
                  </p>
                  <PageLink href={`/account/orders/view?orderId=${order?.number}`} passHref>
                    <span className=' text-70-grey'>#{order?.number}</span>
                  </PageLink>
                </div>

                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className=' text-tight-black'>
                    <Trans id='Order Date' />
                  </p>
                  <span className='text-70-grey'>
                    {dateFormatter.format(new Date(order?.order_date ?? ''))}
                  </span>
                </div>

                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className=' text-tight-black'>
                    <Trans id='Est. ship date' />
                  </p>
                  <span className='text-70-grey'>
                    {order?.estimated_ship_date
                      ? getEstShipDate({
                          estimated_ship_date: order?.estimated_ship_date,
                        })
                      : 'N/A'}
                  </span>
                </div>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className=' text-tight-black'>
                    <Trans id='Ship Method' />
                  </p>
                  <span className='text-70-grey'>{shippingMethodDisp ?? ''}</span>
                </div>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-start md:gap-2'>
                  <p className=' text-tight-black'>
                    <Trans id='Emailed updates' />
                  </p>
                  <span className='text-70-grey'>{firstOrder?.customer_email || ''}</span>
                </div>
              </div>
            </div>
            <div className='hidden md:block'>
              <div className='Type-Large-Regular flex w-full flex-col items-start gap-[4px] md:grid md:grid-cols-5 md:gap-5'>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className='text-tight-black'>
                    <Trans id='Order Number' />
                  </p>
                </div>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className=' text-tight-black'>
                    <Trans id='Order Date' />
                  </p>
                </div>

                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className=' text-tight-black'>
                    <Trans id='Est. ship date' />
                  </p>
                </div>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className=' text-tight-black'>
                    <Trans id='Ship Method' />
                  </p>
                </div>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <p className=' text-tight-black'>
                    <Trans id='Emailed updates' />
                  </p>
                </div>
              </div>
              <hr className='my-2 w-full text-20-grey' />
              <div className='Type-Large-Regular flex w-full flex-col items-start gap-[4px] md:grid md:grid-cols-5 md:gap-5'>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <PageLink href={`/account/orders/view?orderId=${order?.number}`} passHref>
                    <span className=' text-70-grey'>#{order?.number}</span>
                  </PageLink>
                </div>

                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <span className='text-70-grey'>
                    {dateFormatter.format(new Date(order?.order_date ?? ''))}
                  </span>
                </div>

                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <span className='text-70-grey'>
                    {order?.estimated_ship_date
                      ? getEstShipDate({
                          estimated_ship_date: order?.estimated_ship_date,
                        })
                      : 'N/A'}
                  </span>
                </div>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <span className='text-70-grey'>{shippingMethodDisp ?? ''}</span>
                </div>
                <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
                  <Tooltip title={firstOrder?.customer_email || ''} placement='bottom'>
                    <span className='max-w-[120px] truncate whitespace-normal text-70-grey'>
                      {firstOrder?.customer_email || ''}
                    </span>
                  </Tooltip>
                </div>
              </div>
              <hr className='mt-2 w-full text-20-grey' />
            </div>
            {!!firstOrder && (
              <div className='flex w-full flex-col items-center'>
                <div className='Type-XL-Medium grid w-full grid-cols-2 gap-2 md:gap-10'>
                  {firstOrder.billing_address && (
                    <div className='Type-Large-Regular flex w-full flex-col gap-2 md:grid md:grid-cols-[20fr_80fr] md:gap-10'>
                      <h5 className='mb-[10px] text-black'>
                        <Trans id='Billing' />
                      </h5>
                      <CartAddressMultiLine
                        {...firstOrder.billing_address}
                        country={{
                          code: firstOrder.billing_address?.country_code || '',
                          label: firstOrder.billing_address?.country_code || '',
                        }}
                        region={{
                          region_id: parseInt(firstOrder.billing_address.region_id || '', 2) || 0,
                          label: firstOrder.billing_address.region || '',
                        }}
                        __typename='BillingCartAddress'
                        className='Type-Large-Regular text-70-grey'
                      />
                    </div>
                  )}
                  {firstOrder.shipping_address && (
                    <div className='Type-Large-Regular flex w-full flex-col gap-2 md:grid md:grid-cols-[20fr_80fr] md:gap-10'>
                      <h5 className='mb-[10px] text-black'>
                        <Trans id='Shipping' />
                      </h5>
                      <CartAddressMultiLine
                        {...firstOrder.shipping_address}
                        country={{
                          code: firstOrder.shipping_address?.country_code || '',
                          label: firstOrder.shipping_address?.country_code || '',
                        }}
                        region={{
                          region_id: parseInt(firstOrder.shipping_address.region_id || '', 2) || 0,
                          label: firstOrder.shipping_address.region || '',
                        }}
                        className='Type-Large-Regular w-full text-70-grey'
                      />
                    </div>
                  )}
                </div>
                {children}
              </div>
            )}
            <div className='flex flex-col items-center justify-center gap-[70px]'>
              {order?.items?.map((item) => {
                // Second case if for 4over products. their simples don't have images
                const itemImages =
                  images?.[item?.product_url_key ?? ''] ?? Object.values(images)?.at(0)
                return (
                  <div className='flex flex-col items-start justify-center gap-[30px] md:grid md:w-full md:grid-cols-[20fr_80fr]'>
                    <div className=''>
                      <div className='flex h-full w-full flex-col items-center justify-center gap-2'>
                        {itemImages?.thumbnail?.url ? (
                          <div className='flex w-full items-center justify-start rounded-[6px]'>
                            <Box
                              component='a'
                              className=''
                              sx={{
                                overflow: 'hidden',
                                height: '100%',
                                width: '100%',
                                backgroundRepeat: 'no-repeat',
                                aspectRatio: '5/6',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '6px',
                                '@media (min-width: 960px)': {
                                  minHeight: '150px',
                                },
                              }}
                            >
                              <Image
                                alt={itemImages?.thumbnail?.label ?? ''}
                                src={itemImages?.thumbnail?.url ?? ''}
                                unoptimized
                                layout='fill'
                                className='h-full w-auto object-cover'
                                sx={{
                                  aspectRatio: '5/6',
                                  borderRadius: '6px',
                                }}
                              />
                            </Box>
                          </div>
                        ) : (
                          <Box
                            sx={{
                              gridArea: `1 / 1 / 4 / 4`,
                              typography: 'caption',
                              display: 'flex',
                              textAlign: 'center',
                              height: '100%',
                              justifyContent: 'center',
                              alignItems: 'center',
                              color: 'background.default',
                              userSelect: 'none',
                              borderRadius: '6px',
                              aspectRatio: '5/6',
                            }}
                          >
                            <Trans id='No Image' />
                          </Box>
                        )}
                        {item?.artwork && item?.artwork?.length > 0 && (
                          <ItemArtworkManager
                            artwork={item?.artwork}
                            cartItemUid='0'
                            requiresArtwork
                            allowEdit={false}
                          />
                        )}
                      </div>
                    </div>
                    <div className='flex flex-col gap-3'>
                      <h3 className='Type-XL-Regular w-full border-b-[1px] border-20-grey pb-3 text-tight-black'>
                        {item?.product_name}
                      </h3>
                      {item?.quantity_ordered && (
                        <p className='Type-XL-Regular w-full border-b-[1px] border-20-grey pb-3 align-middle text-tight-black'>
                          <Trans
                            id='Quantity: {1}'
                            values={{ 1: item?.quantity_ordered?.toLocaleString() }}
                          />
                        </p>
                      )}
                      <p className='Type-XL-Regular w-full align-middle text-tight-black'>
                        <Trans id='Item details' />
                      </p>
                      <div className='flex flex-col gap-3'>
                        {item &&
                          item.selected_options &&
                          item?.selected_options.map((option) => (
                            <div className='grid grid-cols-2' key={option?.label}>
                              <span className='Type-Large-Regular'>{option?.label}</span>
                              <span className='text-70-grey'>{option?.value}</span>
                            </div>
                          ))}
                        {item &&
                          item.entered_options &&
                          item?.entered_options.map((option) => (
                            <div className='grid grid-cols-2' key={option?.label}>
                              <span className='Type-Large-Regular'>{option?.label}</span>
                              <span className='text-70-grey'>{option?.value}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className='mt-5 flex w-full flex-col items-center justify-center'>
              <div className='Type-XL-Regular grid w-full grid-cols-2 gap-y-2 pb-2 md:w-1/2'>
                <span>
                  <Trans id='Subtotal' />
                </span>
                <span className='text-right'>
                  <Money {...order?.total?.subtotal} />
                </span>
                {!!order?.total?.discounts?.length && (
                  <>
                    <span>
                      <Trans id='Discount' />
                    </span>
                    <span className='text-right'>
                      -<Money {...order?.total?.discounts?.at(0)?.amount} />
                    </span>
                  </>
                )}
                <span>
                  <Trans id='Setup' />
                </span>
                <span className='text-right'>
                  {order?.total?.setup_fees && (
                    <Money
                      value={order?.total?.setup_fees
                        ?.map((fee) => fee?.amount.value)
                        .reduce((a, b) => 1 * (a || 0) + 1 * (b || 0), 0)}
                      currency={order?.total?.grand_total.currency}
                    />
                  )}
                  {!order?.total?.setup_fees && (
                    <Money currency={order?.total?.grand_total.currency} value={0} />
                  )}
                </span>
                <span>
                  <Trans id='Tax' />
                </span>
                <span className='text-right'>
                  {order?.total?.total_tax && <Money {...order?.total?.total_tax} />}
                  {!order?.total?.total_tax && (
                    <Money currency={order?.total?.grand_total.currency} value={0} />
                  )}
                </span>
                <span>
                  <Trans id='Shipping' />
                </span>
                <span className='text-right'>
                  <Money {...order?.total?.total_shipping} />
                </span>
              </div>
              <div className='Type-XXL-Medium grid w-full grid-cols-2 gap-y-2 border-t-[2px] border-20-grey pt-2 md:w-1/2'>
                <span>
                  <Trans id='Total' />
                </span>
                <span className='text-right'>
                  <Money {...order?.total?.grand_total} />
                </span>
              </div>
            </div>
            <div className='mt-10 flex w-full items-center justify-center md:w-auto'>
              <PageLink
                href={`/account/orders/view?orderId=${order?.number}`}
                className='w-full md:w-auto'
              >
                <StandardButton
                  type='button'
                  className='w-full bg-material-ui-blue uppercase md:w-auto'
                  variant='contained'
                  size='small'
                >
                  {i18n._('View Order Status')}
                </StandardButton>
              </PageLink>
            </div>
          </div>
        ))}
      </WaitForCustomer>
    </div>
  )
}
